import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import Pjax from "pjax"
import { Modal } from "../../submodules/rapdv/client/elements/Bootstrap"

type LatLng = { lat: number; lng: number }

export class PageRestaurantDetails implements ClientPage {

  private static readonly DEFAULT_MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty"
  private static readonly PINNED_ZOOM = 16
  private static readonly WORLD_ZOOM = 1
  private static readonly WORLD_CENTER: LatLng = { lat: 20, lng: 0 }
  private static readonly COORDS_DECIMALS = 7
  private static readonly COORDS_DISPLAY_DECIMALS = 5
  private static readonly ADDRESS_FIELD_NAMES = ["street", "city", "postCode"]

  private pjax: Pjax
  private countrySelect: any = null
  private stateSelect: any = null
  private wasUsaBefore: boolean = false
  private map: any = null
  private marker: any = null
  private locationModal: Modal | null = null
  private pinnedLocation: LatLng | null = null

  getPageId = (): string => "restaurant-details-panel"

  execute = (pjax: Pjax): void => {
    this.pjax = pjax

    this.countrySelect = document.querySelector('select[name="country"]') as HTMLSelectElement
    this.stateSelect = document.querySelector('select[name="state"]') as HTMLSelectElement

    this.wasUsaBefore = this.countrySelect?.value === "US"

    this.setupSelectStateElement()

    this.countrySelect?.addEventListener("change", () => {
      this.setupSelectStateElement()
    })

    this.setupRemoveRestaurant()
    this.setupLocationPicker()
    this.setupCoordinatesResetOnAddressChange()
  }

  setupRemoveRestaurant = (): void => {
    const confirmBtn = document.querySelector<HTMLButtonElement>('[data-action="confirm-remove-restaurant"]')
    if (!confirmBtn) return

    confirmBtn.addEventListener("click", async () => {
      const removeUrl = confirmBtn.dataset.removeUrl
      if (!removeUrl) return

      confirmBtn.disabled = true

      try {
        const csrfToken = document.querySelector<HTMLInputElement>('input[name="_csrf"]')?.value ?? ""
        const response = await fetch(removeUrl, {
          method: "DELETE",
          headers: { "X-CSRF-Token": csrfToken },
        })

        if (response.ok) {
          this.pjax.loadUrl("/")
          return
        }
      } catch {
        // Ignore and re-enable so the user can retry
      }

      confirmBtn.disabled = false
    })
  }

  setupSelectStateElement = (): void => {
    const selectedCountryCode = this.countrySelect?.value
    const isUsaNow = selectedCountryCode === "US"

    if (isUsaNow) {
      if (!this.wasUsaBefore) {
        // Deselect state
        this.stateSelect.value = ""
        const event = new Event('change', { bubbles: true });
        this.stateSelect.dispatchEvent(event);
      }
      this.stateSelect?.parentElement?.classList?.remove("d-none")
    } else {
      // Hide and mark as not required
      this.stateSelect?.parentElement?.classList?.add("d-none")
      this.stateSelect.value = "WY" // Select any valid option, it won't get saved anyway
    }

    this.wasUsaBefore = isUsaNow
  }

  setupLocationPicker = (): void => {
    const openButton = document.querySelector<HTMLButtonElement>("[data-location-open]")
    const modalElement = document.getElementById("location-picker-modal")
    if (!openButton || !modalElement) return

    this.locationModal = new Modal(modalElement)

    openButton.addEventListener("click", async () => {
      this.locationModal.show()
      await this.initializeMap()
      await this.showRestaurantLocation()
    })

    modalElement.addEventListener("shown.bs.modal", () => {
      this.map?.resize()
    })

    document.querySelector<HTMLButtonElement>("[data-location-save]")?.addEventListener("click", () => {
      if (!this.pinnedLocation) return
      this.setHiddenCoordinates(this.pinnedLocation)
      this.showPinnedHint()
      this.locationModal?.hide()
    })
  }

  setupCoordinatesResetOnAddressChange = (): void => {
    for (const fieldName of PageRestaurantDetails.ADDRESS_FIELD_NAMES) {
      document.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`)?.addEventListener("change", this.clearPinnedLocation)
    }
    this.countrySelect?.addEventListener("change", this.clearPinnedLocation)
    this.stateSelect?.addEventListener("change", this.clearPinnedLocation)
  }

  private clearPinnedLocation = (): void => {
    const latitudeInput = document.querySelector<HTMLInputElement>("[data-location-latitude]")
    const longitudeInput = document.querySelector<HTMLInputElement>("[data-location-longitude]")
    if (latitudeInput) latitudeInput.value = ""
    if (longitudeInput) longitudeInput.value = ""
    document.querySelector("[data-location-pinned-hint]")?.classList.add("d-none")
  }

  private showRestaurantLocation = async (): Promise<void> => {
    const existingLocation = this.getHiddenCoordinates()
    if (existingLocation) {
      this.moveMarker(existingLocation, true)
      return
    }
    const foundLocation = await this.geocodeFormAddress()
    if (foundLocation) this.moveMarker(foundLocation, true)
  }

  private showPinnedHint = (): void => {
    document.querySelector("[data-location-pinned-hint]")?.classList.remove("d-none")
  }

  private initializeMap = async (): Promise<void> => {
    if (this.map) return

    const mapContainer = document.querySelector<HTMLElement>("[data-location-map]")
    if (!mapContainer) return

    const initialLocation = this.getHiddenCoordinates()
    const center = initialLocation ?? PageRestaurantDetails.WORLD_CENTER
    const zoom = initialLocation ? PageRestaurantDetails.PINNED_ZOOM : PageRestaurantDetails.WORLD_ZOOM
    this.pinnedLocation = center

    const maplibreModule: any = await import("maplibre-gl")
    const maplibre = maplibreModule.default ?? maplibreModule

    this.map = new maplibre.Map({
      container: mapContainer,
      style: mapContainer.dataset.mapStyle || PageRestaurantDetails.DEFAULT_MAP_STYLE_URL,
      center: [center.lng, center.lat],
      zoom,
    })
    this.map.addControl(new maplibre.NavigationControl())

    this.marker = new maplibre.Marker({ draggable: true, color: "#f26125" })
      .setLngLat([center.lng, center.lat])
      .addTo(this.map)

    this.marker.on("dragend", () => {
      const position = this.marker.getLngLat()
      this.pinnedLocation = { lat: position.lat, lng: position.lng }
      this.updateCoordinatesDisplay()
    })

    this.map.on("click", (event: any) => {
      this.moveMarker({ lat: event.lngLat.lat, lng: event.lngLat.lng }, false)
    })

    this.map.resize()
    this.updateCoordinatesDisplay()
  }

  private moveMarker = (location: LatLng, recenter: boolean): void => {
    this.pinnedLocation = location
    this.marker?.setLngLat([location.lng, location.lat])
    if (recenter) this.map?.jumpTo({ center: [location.lng, location.lat], zoom: PageRestaurantDetails.PINNED_ZOOM })
    this.updateCoordinatesDisplay()
  }

  private updateCoordinatesDisplay = (): void => {
    const coordsElement = document.querySelector("[data-location-coords]")
    if (!coordsElement || !this.pinnedLocation) return
    const decimals = PageRestaurantDetails.COORDS_DISPLAY_DECIMALS
    coordsElement.textContent = `${this.pinnedLocation.lat.toFixed(decimals)}, ${this.pinnedLocation.lng.toFixed(decimals)}`
  }

  private getHiddenCoordinates = (): LatLng | null => {
    const latitudeInput = document.querySelector<HTMLInputElement>("[data-location-latitude]")
    const longitudeInput = document.querySelector<HTMLInputElement>("[data-location-longitude]")
    const lat = parseFloat(latitudeInput?.value ?? "")
    const lng = parseFloat(longitudeInput?.value ?? "")
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return { lat, lng }
  }

  private setHiddenCoordinates = (location: LatLng): void => {
    const latitudeInput = document.querySelector<HTMLInputElement>("[data-location-latitude]")
    const longitudeInput = document.querySelector<HTMLInputElement>("[data-location-longitude]")
    const decimals = PageRestaurantDetails.COORDS_DECIMALS
    if (latitudeInput) latitudeInput.value = location.lat.toFixed(decimals)
    if (longitudeInput) longitudeInput.value = location.lng.toFixed(decimals)
  }

  private geocodeFormAddress = async (): Promise<LatLng | null> => {
    const getFieldValue = (name: string): string =>
      document.querySelector<HTMLInputElement>(`[name="${name}"]`)?.value?.trim() ?? ""

    const city = getFieldValue("city")
    if (!city) return null

    return this.geocode({
      street: getFieldValue("street"),
      city,
      state: this.countrySelect?.value === "US" ? this.stateSelect?.value ?? "" : "",
      postCode: getFieldValue("postCode"),
    })
  }

  private geocode = async (params: Record<string, string>): Promise<LatLng | null> => {
    const country = this.countrySelect?.value ?? ""
    const searchParams = new URLSearchParams({ ...params, country })

    try {
      const response = await fetch(`/api/v1/geocode?${searchParams.toString()}`)
      if (!response.ok) return null
      const result = await response.json()
      if (!Number.isFinite(result?.latitude) || !Number.isFinite(result?.longitude)) return null
      return { lat: result.latitude, lng: result.longitude }
    } catch (error) {
      return null
    }
  }

  onPageClose = (): void => {
    this.map?.remove()
    this.map = null
    this.marker = null
    this.locationModal = null
    this.pinnedLocation = null
  }
}
